import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { Point } from 'geojson'
import { useMemo } from 'react'
import AssetGeoPointMap from 'pages/Assets/shared/AssetGeoPointMap'
import { useFacilityDialogGeoContext } from 'pages/Assets/shared/useFacilityDialogGeoContext'
import { resolveStationMapIconIdFromLots } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import { ILot, IStation } from 'utils/interfaces'

import {
  STATION_FORM_TYPE_OPTIONS,
  STATION_ID_FORMAT_HINT,
  stationFormTypeToMapIconId,
  StationFormType,
} from '../../stationTypeUtils'

import style from '../AddStationModal.module.css'

const StationView = ({
  station,
  stationType,
  stationLots,
  isEditMode,
  stationIdError,
  stationNameError,
  facilities,
  facilityId,
  cameraResetKey,
  dialogOpen,
  onNameChanged,
  onStationSNChanged,
  onStationTypeChanged,
  onPointChanged,
  onFacilityChanged,
}: {
  station: IStation
  stationType?: StationFormType
  stationLots?: ILot[]
  isEditMode: boolean
  stationIdError: boolean
  stationNameError: boolean
  facilities: Array<{ id: number; name: string }>
  facilityId: number | null
  cameraResetKey?: string | number
  dialogOpen: boolean
  onNameChanged: (name: string) => void
  onStationSNChanged: (stationId: string) => void
  onStationTypeChanged: (stationType: StationFormType) => void
  onPointChanged: (point: Point) => void
  onFacilityChanged: (facilityId: number | null) => void
}) => {
  const stationTypeOptions: IOption[] = useMemo(
    () => STATION_FORM_TYPE_OPTIONS.map(option => ({ value: option.value, text: option.text })),
    []
  )

  const facilityOptions: IOption[] = useMemo(
    () => facilities.map(facility => ({ value: String(facility.id), text: facility.name })),
    [facilities]
  )

  const stationTypeOption = useMemo(
    () => stationTypeOptions.find(option => option.value === stationType),
    [stationType, stationTypeOptions]
  )

  const facilityOption = useMemo(
    () => facilityOptions.find(option => Number(option.value) === facilityId),
    [facilityId, facilityOptions]
  )

  const { facilityGeoPoint, contextPins, robots, loading } = useFacilityDialogGeoContext(facilityId, dialogOpen, {
    excludeStationId: isEditMode ? station.id : null,
  })

  const mapPins = useMemo(() => {
    if (!station.point) {
      return []
    }
    const lotsForStation =
      stationLots?.filter(lot => lot.robot_station === station.id || lot.robot_station == null) ?? []
    const iconId = isEditMode
      ? resolveStationMapIconIdFromLots(lotsForStation)
      : stationType
        ? stationFormTypeToMapIconId(stationType)
        : undefined
    return [{ point: station.point, lots: lotsForStation, iconId }]
  }, [isEditMode, station.id, station.point, stationLots, stationType])

  const settledCameraKey = loading
    ? 'loading'
    : [
        facilityGeoPoint?.coordinates?.join(',') ?? 'nogeo',
        contextPins.map(pin => pin.id).join('|') || 'nopins',
      ].join(':')

  return (
    <>
      <div className={style.fieldsGrid}>
        <div className={style.fieldsColWide}>
          <TextFieldDescribed
            className={style.textfield}
            description="Station name"
            value={station.name}
            placeholder="Input station name"
            onChange={e => onNameChanged(e.target.value)}
            hasError={stationNameError}
            error="Latin letters, digits, spaces, - and _ only"
            errorMode="tooltip"
          />
          <Dropdown
            className={style.textfield}
            description="Facility"
            currentOption={facilityOption}
            options={facilityOptions}
            placeholder="Select facility"
            onClickOption={(option: IOption) => onFacilityChanged(Number(option.value))}
            menuFitContent
            menuMaxWidth="min(560px, 90vw)"
          />
        </div>
        <div className={style.fieldsColNarrow}>
          <TextFieldDescribed
            className={style.textfield}
            description="Station ID"
            value={station.station_id || ''}
            placeholder={STATION_ID_FORMAT_HINT}
            onChange={e => onStationSNChanged(e.target.value)}
            disabled={isEditMode}
            hasError={stationIdError}
            error={`Required format: ${STATION_ID_FORMAT_HINT}`}
            errorMode="tooltip"
            autoComplete="off"
          />
          <Dropdown
            className={style.textfield}
            description="Station type"
            currentOption={stationTypeOption}
            options={stationTypeOptions}
            placeholder="Select station type"
            onClickOption={(option: IOption) => onStationTypeChanged(option.value as StationFormType)}
            disabled={isEditMode}
            menuFitContent
          />
        </div>
      </div>
      <AssetGeoPointMap
        className={style.map}
        mode={isEditMode ? 'edit' : 'create'}
        point={station.point}
        facilityPoint={facilityGeoPoint}
        pins={mapPins}
        contextPins={contextPins}
        contextRobots={robots}
        contextLoading={loading}
        onPointChanged={onPointChanged}
        cameraResetKey={
          `${cameraResetKey ?? `${isEditMode ? 'edit' : 'create'}-${station.id}-f${facilityId ?? 0}`}:${settledCameraKey}`
        }
      />
    </>
  )
}

export default StationView
